import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDate } from '@/lib/utils';
import { getAccountQmsTickets } from '../api';
import type { QmsTicketSummary, QmsTicketStatus } from '../types';

interface QmsTicketsSectionProps {
  accountId: string;
}

const PAGE_SIZE = 10;

export default function QmsTicketsSection({ accountId }: QmsTicketsSectionProps) {
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['account-qms-tickets', accountId, page, statusFilter],
    queryFn: () =>
      getAccountQmsTickets(accountId, {
        pageNumber: page,
        pageLength: PAGE_SIZE,
        status: statusFilter === 'all' ? undefined : (statusFilter as QmsTicketStatus),
      }),
  });

  if (isLoading) {
    return <QmsTicketsSkeleton />;
  }

  if (error) {
    return (
      <div className="py-10 text-center space-y-4">
        <p className="text-destructive">Error loading QMS tickets</p>
        <p className="text-sm text-muted-foreground">
          {error instanceof Error ? error.message : 'An unexpected error occurred'}
        </p>
        <Button variant="outline" onClick={() => refetch()}>
          Try Again
        </Button>
      </div>
    );
  }

  const tickets = data?.data || [];
  // Note: total is estimated since the API doesn't return exact counts
  // hasMore is determined by whether we got a full page of results
  const hasMore = tickets.length === PAGE_SIZE;

  return (
    <div>
      {/* Filters */}
      <div className="flex items-center justify-end mb-4">
        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v);
            setPage(0);
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="complete">Complete</SelectItem>
            <SelectItem value="validated_closed">Validated</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {tickets.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground">
          No QMS tickets found for this account
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ticket ID</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead>Created</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tickets.map((ticket) => (
                <QmsTicketRow key={ticket._id} ticket={ticket} />
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Page {page + 1}
              {tickets.length > 0 && (
                <span className="ml-1">
                  ({(page * PAGE_SIZE) + 1}-{(page * PAGE_SIZE) + tickets.length} shown)
                </span>
              )}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasMore}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function QmsTicketRow({ ticket }: { ticket: QmsTicketSummary }) {
  // Status badge variants
  const statusVariantMap: Record<
    QmsTicketStatus,
    'secondary' | 'warning' | 'success' | 'outline' | 'default'
  > = {
    submitted: 'warning',
    in_progress: 'secondary',
    complete: 'success',
    validated_closed: 'outline',
  };
  const statusVariant = statusVariantMap[ticket.status] || 'secondary';

  // Status display labels
  const statusLabels: Record<QmsTicketStatus, string> = {
    submitted: 'Submitted',
    in_progress: 'In Progress',
    complete: 'Complete',
    validated_closed: 'Validated',
  };

  // Type badge
  const typeVariant = ticket.type === 'capa' ? 'destructive' : 'outline';
  const typeLabel = ticket.type === 'capa' ? 'CAPA' : 'Ticket';

  // Priority badge (1-5 scale)
  const getPriorityVariant = (
    priority: number
  ): 'destructive' | 'warning' | 'secondary' | 'outline' => {
    if (priority >= 5) return 'destructive';
    if (priority >= 4) return 'warning';
    if (priority >= 3) return 'secondary';
    return 'outline';
  };
  const priorityLabels: Record<number, string> = {
    1: 'Low',
    2: 'Low',
    3: 'Medium',
    4: 'High',
    5: 'Critical',
  };

  // Severity badge (0-2 scale)
  const getSeverityVariant = (
    severity: number
  ): 'destructive' | 'warning' | 'outline' => {
    if (severity >= 2) return 'destructive';
    if (severity >= 1) return 'warning';
    return 'outline';
  };
  const severityLabels: Record<number, string> = {
    0: 'Info',
    1: 'Standard',
    2: 'Major',
  };

  // Get assigned user name
  const getAssignedName = (): string => {
    if (!ticket.assigned_to) {
      return 'Unassigned';
    }
    if (typeof ticket.assigned_to === 'string') {
      return 'Assigned';
    }
    const user = ticket.assigned_to;
    const fullName = `${user.firstname || ''} ${user.lastname || ''}`.trim();
    return fullName || user.email || 'Assigned';
  };

  return (
    <TableRow>
      <TableCell className="font-medium font-mono text-sm">
        {ticket.ticket_id}
      </TableCell>
      <TableCell className="max-w-[200px] truncate" title={ticket.title}>
        {ticket.title || '(No title)'}
      </TableCell>
      <TableCell>
        <Badge variant={typeVariant}>{typeLabel}</Badge>
      </TableCell>
      <TableCell>
        <Badge variant={statusVariant}>{statusLabels[ticket.status]}</Badge>
      </TableCell>
      <TableCell>
        <Badge variant={getPriorityVariant(ticket.priority)}>
          {priorityLabels[ticket.priority] || `P${ticket.priority}`}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant={getSeverityVariant(ticket.severity_level)}>
          {severityLabels[ticket.severity_level] || `L${ticket.severity_level}`}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">{getAssignedName()}</TableCell>
      <TableCell>{formatDate(ticket.created_at)}</TableCell>
      <TableCell>
        <Link
          to={`/qms/tickets/${ticket._id}`}
          className="text-atek-blue hover:underline text-sm"
        >
          View
        </Link>
      </TableCell>
    </TableRow>
  );
}

function QmsTicketsSkeleton() {
  return (
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

export { QmsTicketsSection, QmsTicketRow, QmsTicketsSkeleton };
